import { getFirestore, collection, addDoc, doc, getDoc, getDocs, updateDoc, deleteDoc, query, where, orderBy, serverTimestamp, setDoc, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFirebase } from './firebase';

const app = getFirebase();
const db = getFirestore(app);
const storage = getStorage(app);

// Connect to Firestore emulator in development
if (process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true') {
  try {
    connectFirestoreEmulator(db, 'localhost', 8080);
    console.log('🔥 Firestore Emulator connected');
  } catch (error) {
    console.log('Emulator already connected');
  }
}

// User Operations
export const createUser = async (uid, data) => {
  try {
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, {
      uid,
      ...data,
      created_at: serverTimestamp()
    });
    
    // Məhsul limiti: Hamısı 3 məhsul
    const maxProducts = 3;
    
    const limitRef = doc(db, 'user_limits', uid);
    await setDoc(limitRef, {
      user_id: uid,
      max_products: maxProducts,
      created_at: serverTimestamp()
    });
    
    // Avtomatik mağaza yarat
    if (data.store_name && data.store_name.trim()) {
      const storeRef = doc(collection(db, 'stores'));
      const subdomain = data.store_name.toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .slice(0, 20) + '-' + Date.now().toString().slice(-4);
      
      await setDoc(storeRef, {
        id: storeRef.id,
        owner_id: uid,
        store_name: data.store_name,
        subdomain: subdomain,
        phone_number: data.phone || '',
        store_type: data.store_type || 'retail',
        is_active: true,
        created_at: serverTimestamp()
      });
      
      console.log(`Store auto-created for user ${uid}: ${storeRef.id}`);
    }
    
    console.log(`User created with ${maxProducts} product limit:`, uid);
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const getUser = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid);
    const docSnap = await getDoc(userRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting user:', error);
    return null;
  }
};

export const updateUser = async (uid, data) => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, data);
    console.log('User updated successfully:', uid);
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

// Store Operations
export const createStore = async (data) => {
  const docRef = await addDoc(collection(db, 'stores'), {
    ...data,
    created_at: serverTimestamp()
  });
  return docRef.id;
};

export const getStoreByOwner = async (ownerUid) => {
  const q = query(collection(db, 'stores'), where('owner_uid', '==', ownerUid));
  const snapshot = await getDocs(q);
  if (!snapshot.empty) {
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
  }
  return null;
};

export const getStoreById = async (storeId) => {
  const docRef = doc(db, 'stores', storeId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const storeData = { id: docSnap.id, ...docSnap.data() };
    
    // Əgər phone_number və ya whatsapp_number yoxdursa, owner-in phone-unu götür
    if ((!storeData.phone_number && !storeData.whatsapp_number) && storeData.owner_id) {
      try {
        const ownerRef = doc(db, 'users', storeData.owner_id);
        const ownerSnap = await getDoc(ownerRef);
        if (ownerSnap.exists()) {
          const ownerData = ownerSnap.data();
          if (ownerData.phone) {
            storeData.phone_number = ownerData.phone;
            storeData.whatsapp_number = ownerData.phone;
            console.log('Store phone fallback from owner:', ownerData.phone);
          }
        }
      } catch (error) {
        console.error('Error fetching owner phone:', error);
      }
    } else {
      console.log('Store phone_number:', storeData.phone_number, 'whatsapp_number:', storeData.whatsapp_number);
    }
    
    return storeData;
  }
  return null;
};

export const updateStore = async (storeId, data) => {
  const docRef = doc(db, 'stores', storeId);
  await updateDoc(docRef, data);
};

// Product Operations
export const createProduct = async (data) => {
  const docRef = await addDoc(collection(db, 'products'), {
    ...data,
    created_at: serverTimestamp()
  });
  return docRef.id;
};

export const getProductById = async (productId) => {
  try {
    const docRef = doc(db, 'products', productId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting product:', error);
    return null;
  }
};

export const getProductsByStore = async (storeId) => {
  try {
    // İndex olmadan sadə query
    const q = query(collection(db, 'products'), where('store_id', '==', storeId));
    const snapshot = await getDocs(q);
    const products = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    // Client-side sort
    return products.sort((a, b) => {
      const dateA = a.created_at?.toDate?.() || new Date(0);
      const dateB = b.created_at?.toDate?.() || new Date(0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error getting products:', error);
    return [];
  }
};

export const getProduct = async (productId) => {
  const docRef = doc(db, 'products', productId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return null;
};

export const updateProduct = async (productId, data) => {
  const docRef = doc(db, 'products', productId);
  await updateDoc(docRef, data);
};

export const deleteProduct = async (productId) => {
  const docRef = doc(db, 'products', productId);
  await deleteDoc(docRef);
};

// Order Operations
export const createOrder = async (data) => {
  const docRef = await addDoc(collection(db, 'orders'), {
    ...data,
    created_at: serverTimestamp()
  });
  return docRef.id;
};

export const getOrdersByStore = async (storeId) => {
  try {
    // İndex olmadan sadə query
    const q = query(collection(db, 'orders'), where('store_id', '==', storeId));
    const snapshot = await getDocs(q);
    const orders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    // Client-side sort
    return orders.sort((a, b) => {
      const dateA = a.created_at?.toDate?.() || new Date(0);
      const dateB = b.created_at?.toDate?.() || new Date(0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error getting orders:', error);
    return [];
  }
};

export const updateOrderStatus = async (orderId, status) => {
  const docRef = doc(db, 'orders', orderId);
  await updateDoc(docRef, { status });
};

// Image Upload
export const uploadImage = async (file, path) => {
  const storageRef = ref(storage, `${path}/${Date.now()}_${file.name}`);
  const snapshot = await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(snapshot.ref);
  return downloadURL;
};

// Admin Operations
export const getAllStores = async () => {
  const snapshot = await getDocs(collection(db, 'stores'));
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getAllUsers = async () => {
  const snapshot = await getDocs(collection(db, 'users'));
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getStats = async () => {
  const [usersSnap, storesSnap, productsSnap, ordersSnap] = await Promise.all([
    getDocs(collection(db, 'users')),
    getDocs(collection(db, 'stores')),
    getDocs(collection(db, 'products')),
    getDocs(collection(db, 'orders'))
  ]);
  
  return {
    total_users: usersSnap.size,
    total_stores: storesSnap.size,
    total_products: productsSnap.size,
    total_orders: ordersSnap.size
  };
};

// User Limits Operations
export const getUserLimit = async (userId) => {
  try {
    const docRef = doc(db, 'user_limits', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    }
    // Default limit - 3 məhsul
    return { max_products: 3 };
  } catch (error) {
    console.error('Error getting user limit:', error);
    // Default limit - 3 məhsul
    return { max_products: 3 };
  }
};

export const setUserLimit = async (userId, maxProducts) => {
  try {
    const docRef = doc(db, 'user_limits', userId);
    await setDoc(docRef, {
      user_id: userId,
      max_products: maxProducts,
      updated_at: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error setting user limit:', error);
    throw error;
  }
};

export const updateUserLimit = async (userId, data) => {
  try {
    const docRef = doc(db, 'user_limits', userId);
    await setDoc(docRef, {
      user_id: userId,
      ...data,
      updated_at: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error updating user limit:', error);
    throw error;
  }
};

export const getAllUserLimits = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'user_limits'));
    const limits = {};
    snapshot.docs.forEach(doc => {
      limits[doc.id] = doc.data();
    });
    return limits;
  } catch (error) {
    console.error('Error getting all user limits:', error);
    return {};
  }
};

// Get product count for a store
export const getProductCountByStore = async (storeId) => {
  try {
    const q = query(collection(db, 'products'), where('store_id', '==', storeId));
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('Error getting product count:', error);
    return 0;
  }
};

// Check if user can add more products
export const canAddProduct = async (userId, storeId) => {
  try {
    const [userLimit, productCount] = await Promise.all([
      getUserLimit(userId),
      getProductCountByStore(storeId)
    ]);
    return {
      canAdd: productCount < userLimit.max_products,
      currentCount: productCount,
      maxProducts: userLimit.max_products
    };
  } catch (error) {
    console.error('Error checking product limit:', error);
    return { canAdd: false, currentCount: 0, maxProducts: 10 };
  }
};

// Get stores with user info for admin
export const getStoresWithOwners = async () => {
  try {
    const [storesSnap, usersSnap, limitsSnap] = await Promise.all([
      getDocs(collection(db, 'stores')),
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'user_limits'))
    ]);

    const users = {};
    usersSnap.docs.forEach(doc => {
      users[doc.id] = { id: doc.id, ...doc.data() };
    });

    const limits = {};
    limitsSnap.docs.forEach(doc => {
      limits[doc.id] = doc.data();
    });

    const stores = await Promise.all(storesSnap.docs.map(async (storeDoc) => {
      const storeData = { id: storeDoc.id, ...storeDoc.data() };
      const owner = users[storeData.owner_uid] || null;
      const userLimit = limits[storeData.owner_uid] || { max_products: 3 };
      
      // Get product count
      const productsQ = query(collection(db, 'products'), where('store_id', '==', storeDoc.id));
      const productsSnap = await getDocs(productsQ);
      
      return {
        ...storeData,
        owner,
        products_count: productsSnap.size,
        max_products: userLimit.max_products
      };
    }));

    return stores;
  } catch (error) {
    console.error('Error getting stores with owners:', error);
    return [];
  }
};

// Plans Operations
export const getAllPlans = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'plans'));
    const plans = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    // Sort by price
    return plans.sort((a, b) => (a.price_monthly || 0) - (b.price_monthly || 0));
  } catch (error) {
    console.error('Error getting plans:', error);
    return [];
  }
};

export const getPlanById = async (planId) => {
  try {
    const docRef = doc(db, 'plans', planId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting plan:', error);
    return null;
  }
};

export const createPlan = async (data) => {
  const docRef = await addDoc(collection(db, 'plans'), {
    ...data,
    created_at: serverTimestamp()
  });
  return docRef.id;
};

export const updatePlan = async (planId, data) => {
  const docRef = doc(db, 'plans', planId);
  await updateDoc(docRef, {
    ...data,
    updated_at: serverTimestamp()
  });
};

export const deletePlan = async (planId) => {
  const docRef = doc(db, 'plans', planId);
  await deleteDoc(docRef);
};

// Site Settings Operations
export const getSiteSettings = async () => {
  try {
    const docRef = doc(db, 'site_settings', 'main');
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    }
    return null;
  } catch (error) {
    console.error('Error getting site settings:', error);
    return null;
  }
};

export const updateSiteSettings = async (data) => {
  try {
    const docRef = doc(db, 'site_settings', 'main');
    await setDoc(docRef, {
      ...data,
      updated_at: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error updating site settings:', error);
    throw error;
  }
};

// Partners Operations
export const getAllPartners = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'partners'));
    const partners = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return partners.sort((a, b) => (a.order || 0) - (b.order || 0));
  } catch (error) {
    console.error('Error getting partners:', error);
    return [];
  }
};

export const createPartner = async (data) => {
  const docRef = await addDoc(collection(db, 'partners'), {
    ...data,
    created_at: serverTimestamp()
  });
  return docRef.id;
};

export const updatePartner = async (partnerId, data) => {
  const docRef = doc(db, 'partners', partnerId);
  await updateDoc(docRef, {
    ...data,
    updated_at: serverTimestamp()
  });
};

export const deletePartner = async (partnerId) => {
  const docRef = doc(db, 'partners', partnerId);
  await deleteDoc(docRef);
};

// Store Customization Operations
export const getStoreCustomization = async (storeId) => {
  try {
    const docRef = doc(db, 'store_customization', storeId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting store customization:', error);
    return null;
  }
};

export const updateStoreCustomization = async (storeId, data) => {
  try {
    const docRef = doc(db, 'store_customization', storeId);
    await setDoc(docRef, {
      store_id: storeId,
      ...data,
      updated_at: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error updating store customization:', error);
    throw error;
  }
};

// Banner Operations
export const getStoreBanners = async (storeId) => {
  try {
    const q = query(
      collection(db, 'store_banners'),
      where('store_id', '==', storeId)
    );
    const snapshot = await getDocs(q);
    const banners = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    // Client-side sort by order
    return banners.sort((a, b) => (a.order || 0) - (b.order || 0));
  } catch (error) {
    console.error('Error getting store banners:', error);
    return [];
  }
};

export const createStoreBanner = async (data) => {
  const docRef = await addDoc(collection(db, 'store_banners'), {
    ...data,
    created_at: serverTimestamp()
  });
  return docRef.id;
};

export const updateStoreBanner = async (bannerId, data) => {
  const docRef = doc(db, 'store_banners', bannerId);
  await updateDoc(docRef, {
    ...data,
    updated_at: serverTimestamp()
  });
};

export const deleteStoreBanner = async (bannerId) => {
  const docRef = doc(db, 'store_banners', bannerId);
  await deleteDoc(docRef);
};

// Delete Store with all related data
export const deleteStore = async (storeId) => {
  try {
    console.log('Deleting store:', storeId);
    
    // First delete the store itself
    const storeRef = doc(db, 'stores', storeId);
    await deleteDoc(storeRef);
    console.log('Store document deleted');

    // Then try to delete related data (ignore errors)
    try {
      const productsQ = query(collection(db, 'products'), where('store_id', '==', storeId));
      const productsSnap = await getDocs(productsQ);
      for (const productDoc of productsSnap.docs) {
        await deleteDoc(productDoc.ref).catch(() => {});
      }
      console.log('Products deleted');
    } catch (e) {
      console.log('Products delete skipped:', e.message);
    }

    try {
      const bannersQ = query(collection(db, 'store_banners'), where('store_id', '==', storeId));
      const bannersSnap = await getDocs(bannersQ);
      for (const bannerDoc of bannersSnap.docs) {
        await deleteDoc(bannerDoc.ref).catch(() => {});
      }
    } catch (e) {
      console.log('Banners delete skipped:', e.message);
    }

    try {
      const customRef = doc(db, 'store_customization', storeId);
      await deleteDoc(customRef).catch(() => {});
    } catch (e) {
      console.log('Customization delete skipped:', e.message);
    }

    try {
      const categoriesQ = query(collection(db, 'store_categories'), where('store_id', '==', storeId));
      const categoriesSnap = await getDocs(categoriesQ);
      for (const catDoc of categoriesSnap.docs) {
        await deleteDoc(catDoc.ref).catch(() => {});
      }
    } catch (e) {
      console.log('Categories delete skipped:', e.message);
    }

    console.log('Store and related data deleted:', storeId);
    return true;
  } catch (error) {
    console.error('Error deleting store:', error);
    throw error;
  }
};

// Category Operations
export const getStoreCategories = async (storeId) => {
  try {
    const q = query(
      collection(db, 'store_categories'),
      where('store_id', '==', storeId)
    );
    const snapshot = await getDocs(q);
    const categories = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return categories.sort((a, b) => (a.order || 0) - (b.order || 0));
  } catch (error) {
    console.error('Error getting store categories:', error);
    return [];
  }
};

export const createStoreCategory = async (data) => {
  const docRef = await addDoc(collection(db, 'store_categories'), {
    ...data,
    created_at: serverTimestamp()
  });
  return docRef.id;
};

export const updateStoreCategory = async (categoryId, data) => {
  const docRef = doc(db, 'store_categories', categoryId);
  await updateDoc(docRef, {
    ...data,
    updated_at: serverTimestamp()
  });
};

export const deleteStoreCategory = async (categoryId) => {
  const docRef = doc(db, 'store_categories', categoryId);
  await deleteDoc(docRef);
};

// Subdomain Operations
export const checkSubdomainAvailability = async (subdomain) => {
  try {
    const normalizedSubdomain = subdomain.toLowerCase().trim();
    
    // Rezerv subdomain-lər
    const reserved = ['www', 'admin', 'api', 'app', 'mail', 'ftp', 'test', 'dev', 'staging', 'blog', 'shop', 'store'];
    if (reserved.includes(normalizedSubdomain)) {
      return false;
    }
    
    const q = query(collection(db, 'stores'), where('subdomain', '==', normalizedSubdomain));
    const snapshot = await getDocs(q);
    return snapshot.empty;
  } catch (error) {
    console.error('Error checking subdomain:', error);
    return false;
  }
};

export const getStoreBySubdomain = async (subdomain) => {
  try {
    const normalizedSubdomain = subdomain.toLowerCase().trim();
    const q = query(collection(db, 'stores'), where('subdomain', '==', normalizedSubdomain));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting store by subdomain:', error);
    return null;
  }
};

export const getStoreByCustomDomain = async (domain) => {
  try {
    const normalizedDomain = domain.toLowerCase().trim();
    const q = query(
      collection(db, 'stores'), 
      where('custom_domain', '==', normalizedDomain),
      where('custom_domain_enabled', '==', true)
    );
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting store by custom domain:', error);
    return null;
  }
};

// Review Operations
export const createReview = async (data) => {
  const docRef = await addDoc(collection(db, 'reviews'), {
    ...data,
    approved: false,
    created_at: serverTimestamp()
  });
  return docRef.id;
};

export const getAllReviews = async () => {
  try {
    const q = query(collection(db, 'reviews'));
    const snapshot = await getDocs(q);
    const reviews = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    // Sort in memory by created_at
    return reviews.sort((a, b) => {
      if (!a.created_at || !b.created_at) return 0;
      return b.created_at.seconds - a.created_at.seconds;
    });
  } catch (error) {
    console.error('Error getting reviews:', error);
    return [];
  }
};

export const getApprovedReviews = async () => {
  try {
    console.log('Fetching approved reviews...');
    const reviewsRef = collection(db, 'reviews');
    const snapshot = await getDocs(reviewsRef);
    
    console.log('Total reviews in database:', snapshot.docs.length);
    
    const allReviews = snapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    }));
    
    console.log('All reviews:', allReviews);
    
    // Filter approved reviews
    const approvedReviews = allReviews.filter(review => review.approved === true);
    
    console.log('Approved reviews:', approvedReviews);
    
    // Sort by created_at if available
    approvedReviews.sort((a, b) => {
      if (!a.created_at || !b.created_at) return 0;
      const aTime = a.created_at.seconds || a.created_at;
      const bTime = b.created_at.seconds || b.created_at;
      return bTime - aTime;
    });
    
    return approvedReviews.slice(0, 10);
  } catch (error) {
    console.error('Error getting approved reviews:', error);
    return [];
  }
};

export const deleteReview = async (reviewId) => {
  const docRef = doc(db, 'reviews', reviewId);
  await deleteDoc(docRef);
};

export const approveReview = async (reviewId) => {
  console.log('Approving review:', reviewId);
  const docRef = doc(db, 'reviews', reviewId);
  await updateDoc(docRef, {
    approved: true,
    updated_at: serverTimestamp()
  });
  console.log('Review approved successfully');
};

